import type { FormValue, Question } from '../../types';
import type { RepeatBindings } from '../primitives/repeatBindings';
import { evaluateRepeatItemEq } from '../primitives/repeatItemEq';
import { resolveYesNoOptionPair } from '../primitives/yesNoRadio';
import type { ConditionalWhenClause, ConditionalNode } from './types';

function isAbsentEqAnswer(raw: FormValue | undefined): boolean {
  if (raw === undefined || raw === null) return true;
  if (typeof raw === 'string') return raw.trim() === '';
  return false;
}

/** Resolve `derived[id]` from layout `sum` nodes; unknown ids behave as `0`. */
export function lookupDerivedNumeric(
  derived: Readonly<Record<string, number>>,
  id: string
): number {
  const v = derived[id];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/** True when a single `conditional` / `disabled-when` clause matches current answers and repeat scope. */
export function whenClauseSatisfied(
  when: ConditionalWhenClause,
  answers: Record<string, FormValue>,
  repeatBindings: RepeatBindings,
  derived: Readonly<Record<string, number>> = {},
  schemaMap?: ReadonlyMap<string, Question>
): boolean {
  if ('kind' in when && when.kind === 'repeatItemEq') {
    return evaluateRepeatItemEq(when.binding, when.eq, repeatBindings);
  }

  if ('derived' in when && typeof when.derived === 'string') {
    const n = lookupDerivedNumeric(derived, when.derived);
    if ('between' in when && when.between != null) {
      const {
        min,
        max,
        minInclusive = true,
        maxInclusive = false,
      } = when.between;
      const geMin = minInclusive ? n >= min : n > min;
      const leMax = maxInclusive ? n <= max : n < max;
      return geMin && leMax;
    }
    if ('lt' in when) return n < when.lt;
    if ('lte' in when) return n <= when.lte;
    if ('gt' in when) return n > when.gt;
    if ('gte' in when) return n >= when.gte;
    return false;
  }

  if ('includes' in when) {
    const val = answers[when.qid];
    return Array.isArray(val) && val.includes(when.includes);
  }
  if ('qid' in when && 'in' in when) {
    const stored = answers[when.qid];
    if (when.in.length === 0) return false;
    // Radio/select: answer is one of `in`. Multiselect: any selected value is
    // in `in` (same kids under several parent options — one gate, one block).
    if (typeof stored === 'string') return when.in.includes(stored);
    if (Array.isArray(stored)) {
      return stored.some(v => typeof v === 'string' && when.in.includes(v));
    }
    return false;
  }
  if ('qid' in when && 'eq' in when) {
    const stored = answers[when.qid];
    if (stored === when.eq) return true;
    if (!schemaMap || !isAbsentEqAnswer(stored)) return false;
    const pair = resolveYesNoOptionPair(schemaMap.get(when.qid)?.values);
    if (!pair) return false;
    const want = when.eq.trim();
    const no = pair.noValue.trim();
    return no === want || no.toLowerCase() === want.toLowerCase();
  }
  return false;
}

/** Schema QIDs referenced by a single gate clause (`conditional`, `disabled-when`, table row). */
export function conditionalWhenClauseRefQids(
  when: ConditionalWhenClause
): string[] {
  if ('kind' in when && when.kind === 'repeatItemEq') return [];
  if ('derived' in when) return [];
  if ('qid' in when) return [when.qid];
  return [];
}

/** Add every schema QID referenced by gate clauses into `target`. */
export function addConditionalClauseRefQidsToSet(
  target: Set<string>,
  clauses: ConditionalWhenClause[]
): void {
  for (const clause of clauses) {
    for (const qid of conditionalWhenClauseRefQids(clause)) {
      target.add(qid);
    }
  }
}

/** Add QIDs from a `when` / `whenAll` gate (`conditional`, table row, etc.). */
export function addWhenGateRefQidsToSet(
  target: Set<string>,
  node: Pick<ConditionalNode, 'when' | 'whenAll'>
): void {
  addConditionalClauseRefQidsToSet(target, resolveConditionalClauses(node));
}

/** Clauses for a `conditional` node — `whenAll` wins when non-empty. */
export function resolveConditionalClauses(
  node: Pick<ConditionalNode, 'when' | 'whenAll'>
): ConditionalWhenClause[] {
  if (Array.isArray(node.whenAll) && node.whenAll.length > 0) {
    return node.whenAll;
  }
  if (node.when != null) {
    return [node.when];
  }
  return [];
}

/** True when every clause on a conditional gate holds (empty clause list → true). */
export function conditionalWhenSatisfied(
  node: Pick<ConditionalNode, 'when' | 'whenAll'>,
  answers: Record<string, FormValue>,
  repeatBindings: RepeatBindings,
  derived: Readonly<Record<string, number>> = {},
  schemaMap?: ReadonlyMap<string, Question>
): boolean {
  const clauses = resolveConditionalClauses(node);
  if (clauses.length === 0) return true;
  return clauses.every(w =>
    whenClauseSatisfied(w, answers, repeatBindings, derived, schemaMap)
  );
}
